import { required } from "ra-core";
import { BooleanInput } from "@/components/admin/boolean-input";
import { Create } from "@/components/admin/create";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { Edit } from "@/components/admin/edit";
import { List } from "@/components/admin/list";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextField } from "@/components/admin/text-field";
import { TextInput } from "@/components/admin/text-input";
import { Badge } from "@/components/ui/badge";
import { MobilePage } from "../MobilePage";
import { useIsDueno } from "../useIsDueno";
import { HelpTooltip } from "../help/HelpTooltip";

const CHANNELS = [
  { id: "llamada", name: "Llamada (guion)" },
  { id: "whatsapp", name: "WhatsApp" },
  { id: "email", name: "Correo" },
];

const filters = [
  <ReferenceInput
    key="offer"
    source="offer_id"
    reference="offers"
    sort={{ field: "priority", order: "ASC" }}
    alwaysOn
  >
    <SelectInput label="Oferta" optionText="name" emptyText="Todas" />
  </ReferenceInput>,
  <SelectInput
    key="channel"
    source="channel"
    label="Canal"
    choices={CHANNELS}
  />,
];

export const TemplateList = () => {
  // Raftel's catalog is read-only for freelancers (E6.4): only the owner creates or edits
  const isDueno = useIsDueno();
  return (
    <MobilePage title="Plantillas">
      <List
        title="Plantillas"
        filters={filters}
        exporter={false}
        sort={{ field: "offer_id", order: "ASC" }}
        perPage={50}
        disableBreadcrumb
        actions={isDueno ? <CreateButton label="Nueva plantilla" /> : false}
      >
        <DataTable
          bulkActionButtons={false}
          rowClick={isDueno ? "edit" : false}
        >
          <DataTable.Col label="Oferta" source="offer_id">
            <ReferenceField source="offer_id" reference="offers" link={false}>
              <TextField source="name" className="font-medium" />
            </ReferenceField>
          </DataTable.Col>
          <DataTable.Col
            label="Canal"
            source="channel"
            render={(r) => CHANNELS.find((c) => c.id === r.channel)?.name}
          />
          <DataTable.Col
            label="Nombre"
            source="name"
            render={(r) => (
              <span>
                {r.name}{" "}
                {!r.active && <Badge variant="outline">inactiva</Badge>}{" "}
                {r.pending_validation && (
                  <Badge variant="outline" className="border-[var(--th-3)]">
                    pendiente de validar
                  </Badge>
                )}
              </span>
            )}
          />
        </DataTable>
      </List>
    </MobilePage>
  );
};

const TemplateForm = () => (
  <SimpleForm>
    <ReferenceInput
      source="offer_id"
      reference="offers"
      sort={{ field: "priority", order: "ASC" }}
    >
      <SelectInput label="Oferta" optionText="name" validate={required()} />
    </ReferenceInput>
    <SelectInput
      source="channel"
      label="Canal"
      choices={CHANNELS}
      validate={required()}
    />
    <TextInput source="name" label="Nombre" validate={required()} />
    <TextInput source="subject" label="Asunto (solo correo)" />
    <TextInput
      source="body"
      label={
        <span className="inline-flex items-center gap-1">
          Texto
          <HelpTooltip topic="templates.variables" />
        </span>
      }
      multiline
      rows={8}
      validate={required()}
      helperText="Variables: {{empresa}} {{nombre}} {{giro}} {{municipio}} {{remitente}} {{agenda}}. Si falta, se agrega sola la opción de responder BAJA (en correo, siempre al final)."
    />
    <BooleanInput source="active" label="Activa" defaultValue={true} />
    <BooleanInput
      source="pending_validation"
      label="Pendiente de validar (texto base con promesas por revisar)"
      defaultValue={false}
    />
  </SimpleForm>
);

export const TemplateCreate = () => (
  <MobilePage title="Nueva plantilla">
    <Create title="Nueva plantilla" redirect="list">
      <TemplateForm />
    </Create>
  </MobilePage>
);

export const TemplateEdit = () => (
  <MobilePage title="Editar plantilla">
    <Edit title="Editar plantilla" redirect="list" mutationMode="pessimistic">
      <TemplateForm />
    </Edit>
  </MobilePage>
);
